import React from 'react';
import { View, Text } from '@smarterservices/smarter-ui';

/**
 * DeviceSelector Component
 * 
 * A simple device selector that uses native browser APIs
 * instead of LiveKit components to avoid hook context issues.
 */
export const DeviceSelector = ({ kind, onDeviceChange, label, initialDeviceLabel }) => {
    const [devices, setDevices] = React.useState([]);
    const [selectedDevice, setSelectedDevice] = React.useState('');
    const [hasPermission, setHasPermission] = React.useState(false);
    const [error, setError] = React.useState(null);

    // Request permissions and enumerate devices
    React.useEffect(() => {
        const requestPermissionsAndEnumerate = async () => {
            try {
                console.log(`[DeviceSelector] Requesting ${kind} devices...`);

                // Request permissions first
                const constraints = kind === 'audioinput'
                    ? { audio: true }
                    : kind === 'videoinput'
                        ? { video: true }
                        : null;

                if (constraints) {
                    console.log(`[DeviceSelector] Requesting getUserMedia for ${kind}...`);
                    const stream = await navigator.mediaDevices.getUserMedia(constraints);
                    console.log(`[DeviceSelector] Got stream for ${kind}, stopping tracks...`);
                    // Stop the stream immediately - we just needed permissions
                    stream.getTracks().forEach(track => track.stop());
                    setHasPermission(true);
                } else {
                    // audiooutput doesn't need permissions
                    console.log(`[DeviceSelector] ${kind} doesn't need permissions`);
                    setHasPermission(true);
                }

                // Now enumerate devices with labels
                console.log(`[DeviceSelector] Enumerating devices for ${kind}...`);
                const deviceList = await navigator.mediaDevices.enumerateDevices();
                console.log(`[DeviceSelector] Found ${deviceList.length} total devices`);

                const filteredDevices = deviceList.filter(d => d.kind === kind);
                console.log(`[DeviceSelector] Found ${filteredDevices.length} ${kind} devices:`, filteredDevices);

                setDevices(filteredDevices);

                // Set default device (use initialDeviceLabel if provided and exists, otherwise use default)
                if (filteredDevices.length > 0) {
                    let deviceToSelect;

                    if (initialDeviceLabel) {
                        // Check if the initial device still exists by matching label
                        deviceToSelect = filteredDevices.find(d => d.label === initialDeviceLabel);
                        console.log(`[DeviceSelector] Looking for device with label "${initialDeviceLabel}":`, deviceToSelect ? 'Found' : 'Not found');
                    }

                    if (!deviceToSelect) {
                        // Fall back to default or first device
                        deviceToSelect = filteredDevices.find(d => d.deviceId === 'default') || filteredDevices[0];
                        console.log(`[DeviceSelector] Using fallback device:`, deviceToSelect?.label);
                    }

                    setSelectedDevice(deviceToSelect.deviceId);
                    console.log(`[DeviceSelector] Selected device:`, deviceToSelect);
                } else {
                    console.warn(`[DeviceSelector] No ${kind} devices found!`);
                }
            } catch (err) {
                console.error(`[DeviceSelector] Failed to get ${kind} devices:`, err);
                setError(err.message);
                setHasPermission(false);
            }
        };

        requestPermissionsAndEnumerate();

        // Listen for device changes
        const handleDeviceChange = () => {
            requestPermissionsAndEnumerate();
        };

        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
        };
    }, [kind]);

    const handleChange = (event) => {
        const deviceId = event.target.value;
        setSelectedDevice(deviceId);

        if (onDeviceChange) {
            // Find the device to get its label
            const device = devices.find(d => d.deviceId === deviceId);
            const deviceLabel = device?.label || `${kind} ${deviceId.substring(0, 8)}`;
            onDeviceChange(deviceId, deviceLabel);
        }
    };

    if (error) {
        return (
            <View as="div" padding="small">
                <Text size="small" color="danger">
                    Error: {error}
                </Text>
            </View>
        );
    }

    if (!hasPermission) {
        return (
            <View as="div" padding="small">
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    {label}
                </label>
                <select
                    disabled
                    style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '14px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5'
                    }}
                >
                    <option value="">Requesting permissions...</option>
                </select>
            </View>
        );
    }

    if (devices.length === 0) {
        return (
            <View as="div" padding="small">
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    {label}
                </label>
                <select
                    disabled
                    style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '14px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5'
                    }}
                >
                    <option value="">No devices found</option>
                </select>
            </View>
        );
    }

    return (
        <View as="div" padding="small">
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                {label}
            </label>
            <select
                value={selectedDevice}
                onChange={handleChange}
                style={{
                    width: '100%',
                    padding: '8px',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                }}
            >
                {devices.map((device) => (
                    <option
                        key={device.deviceId}
                        value={device.deviceId}
                    >
                        {device.label || `${kind} ${device.deviceId.substring(0, 8)}`}
                    </option>
                ))}
            </select>
        </View>
    );
};
